import type { AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";
import JwtService from "@/ts/core/services/JwtService";
import { ApiResponseCode } from "@/ts/enums/ApiResponseCode";

/**
 * @description service to call HTTP request via Axios
 * Manager 페이지의 ApiService를 커스텀
 */
class ApiService {
	/**
	 * @description property to share vue instance
	 */
	public axiosInstance = axios;

	/**
	 * @description initialize vue axios
	 */
	public constructor() {
		this.setHeader();
		this.setInterceptors();
	}

	/**
	 * @description set the default HTTP request headers
	 */
	public setHeader(): void {
		const token = this.getToken();
		this.axiosInstance.defaults.headers.common[
			"Authorization"
		] = `Bearer ${token}`;
		this.axiosInstance.defaults.headers.common["Accept"] =
			"application/json";

		this.axiosInstance.defaults.headers.common['App-Language'] = this.getLang();
	}

	public getLang(): string {
		let app_language = document.head.querySelector('meta[name="App-Language"]') as HTMLMetaElement;

		if(app_language) {
			console.log("app_language", app_language.content);
			return app_language.content;
		} else {
			console.warn('app language header can not found');
			return 'ko';
		}
	}

	public getToken() :string {
		let headerToken = document.head.querySelector('meta[name="App-Access-Token"]') as HTMLMetaElement;
		return headerToken.content;
	}

	public setToken(token:string) {
		console.log("set new Toekn to header")
		this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
		
		let headerToken = document.head.querySelector('meta[name="App-Access-Token"]') as HTMLMetaElement;
		headerToken.content = token;
	}

	public setInterceptors() {

		// 응답 인터셉터 - access_token 만료 시 refresh 로직
		this.axiosInstance.interceptors.response.use(
			(response) => response,
			async (error) => {
				console.log(error);
				const originalRequest = error.config;
				const code = error.response?.data?.code;
				// 401 에러이며 재시도하지 않은 요청일 경우만 처리
				console.log(error.response?.status === 401 , code , !originalRequest._retry)
				if (error.response?.status === 401 && code === ApiResponseCode.TOKEN_EXPIRED && !originalRequest._retry) {

					originalRequest._retry = true;

					try {
						// refresh 요청
						const res = await this.axiosInstance.post(`${process.env.MIX_API_HOST}/auth/refresh`, {});

						const newAccessToken = res.data.data.access_token;

						// 저장하고 원래 요청에 다시 붙여서 재시도
						localStorage.setItem('access_token', newAccessToken);
						this.setToken(newAccessToken)
						originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

						

						return this.axiosInstance(originalRequest);
					} catch (refreshError) {
						// 재발급 실패 시 강제 로그아웃 또는 알림
						console.error('토큰 재발급 실패:', refreshError);
						localStorage.removeItem('access_token');
						localStorage.removeItem('refresh_token');
						// window.location.href = '/login'; // 또는 custom alert
						return Promise.reject(refreshError);
					}
				}

				return Promise.reject(error);
			}
		);
	}

	/**
	 * @description send the GET HTTP request
	 * @param resource: string
	 * @param params: AxiosRequestConfig
	 * @returns Promise<AxiosResponse>
	 */
	public query(resource: string, params: any): Promise<AxiosResponse> {
		return this.axiosInstance.get(resource, params);
	}

	/**
	 * @description send the GET HTTP request
	 * @param resource: string
	 * @param slug: string
	 * @returns Promise<AxiosResponse>
	 */
	public get(
		resource: string,
		slug = "" as string
	): Promise<AxiosResponse> {
		return this.axiosInstance.get(`${resource}/${slug}`);
	}

	/**
	 * @description set the POST HTTP request
	 * @param resource: string
	 * @param params: AxiosRequestConfig
	 * @returns Promise<AxiosResponse>
	 */
	public post(resource: string, params: any, config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return this.axiosInstance.post(`${resource}`, params, config);
	}

	/**
	 * @description send the UPDATE HTTP request
	 * @param resource: string
	 * @param slug: string
	 * @param params: AxiosRequestConfig
	 * @returns Promise<AxiosResponse>
	 */
	public update(
		resource: string,
		slug: string,
		params: any
	): Promise<AxiosResponse> {
		return this.axiosInstance.put(`${resource}/${slug}`, params);
	}

	/**
	 * @description Send the PUT HTTP request
	 * @param resource: string
	 * @param params: AxiosRequestConfig
	 * @returns Promise<AxiosResponse>
	 */
	public put(resource: string, params: any): Promise<AxiosResponse> {
		return this.axiosInstance.put(`${resource}`, params);
	}

	/**
	 * @description Send the DELETE HTTP request
	 * @param resource: string
	 * @returns Promise<AxiosResponse>
	 */
	public delete(resource: string): Promise<AxiosResponse> {
		return this.axiosInstance.delete(resource);
	}
}

export default new ApiService;
